import React from 'react';
import { SelectFieldComponent } from '../common/SelectFieldComponent';
import { useProcedencias } from '../../hooks/useParametric';
import { type Procedencia } from '../../types/catalog';

export interface ProcedenciaSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter procedencias */
  filter?: (procedencia: Procedencia) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

/**
 * ProcedenciaSelect Component
 *
 * A reusable dropdown component that displays procedencias from parametric data.
 * Features:
 * - Automatic data loading from parametric store
 * - Loading states and error handling
 * - Filtering capabilities
 * - Full TypeScript support
 * - Consistent styling with other form components
 */
export const ProcedenciaSelect: React.FC<ProcedenciaSelectProps> = ({
  value,
  onChange,
  label = 'Procedencia',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  sx,
}) => {
  const { data: procedencias, loading, fetch: fetchProcedencias } = useProcedencias();

  // Fetch procedencias on component mount
  React.useEffect(() => {
    fetchProcedencias();
  }, [fetchProcedencias]);

  // Filter procedencias based on props
  const filteredProcedencias = React.useMemo(() => {
    let filtered = procedencias;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [procedencias, filter]);

  // Convert procedencias to select options
  const options = React.useMemo(() => {
    return filteredProcedencias.map((procedencia) => ({
      value: procedencia.idProcedencia,
      label: procedencia.descripcion,
    }));
  }, [filteredProcedencias]);

  // Handle error state
  const displayError = error;

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      options={options}
      error={displayError}
      disabled={disabled || loading}
      placeholder={placeholder}
      loading={loading}
      sx={sx}
    />
  );
};

export default ProcedenciaSelect;